/**
 * The machine's projects for the remote Projects view, from three sources merged by project id
 * and root id:
 *
 *   1. `projects.list` (Control API, newer): every project on the machine, open or not, every
 *      root with its target and host state. The live picture; undefined on an app without it.
 *   2. The platform (`GET /projects/v1`, `GET /projects/v1/:id`): the organisation's projects the
 *      person may see, each root with the device that registered it, its remote and branch, and
 *      the host state that device last reported. A root counts when its `deviceId` is this one.
 *   3. `workspaces.list`: the roots whose host runs now (state ready or stopped, the path). An
 *      overlay on whichever roots it names; one it names that matches nothing is kept on its own.
 *
 * Pure functions, so the merge can be read (and tested) without a workbench.
 */

import type { ProjectRoot } from '@aioe/shared';

import type { LiveProject, LiveProjectRoot, Workspace } from './useWorkbench.ts';

export interface MachineRoot {
  rootId: string;
  name: string;
  remote?: string;
  branch?: string;
  target?: string;
  targetKind?: string;
  distro?: string;
  hostState?: string;
  targetState?: string;
  /** When a machine last reported it with its host running (platform). */
  lastSeenAt?: string;
  /** The id `workspaces.list` and the `workspace` parameter use, while a host runs (`projects.list`). */
  workspaceId?: string;
  /** The live workspace for this root, when its host runs. */
  workspace?: Workspace;
  /** The app answered for this root just now (`projects.list` or `workspaces.list`), not only the platform's record. */
  live: boolean;
}

export interface MachineProject {
  id: string;
  name: string;
  open: boolean;
  /** Known to the organisation (the platform lists it), as opposed to a project only on this machine. */
  organisation: boolean;
  roots: MachineRoot[];
}

export interface PlatformProject {
  id: string;
  name: string;
  roots: ProjectRoot[];
}

/** A registration's workspace (`DeviceRow.workspaces`): its id is the root id, and it carries the target kind. */
export interface RegisteredWorkspace {
  id: string;
  name: string;
  projectId?: string;
  target?: string;
  remote?: string;
  branch?: string;
  hostState?: string;
  distro?: string;
  targetState?: string;
}

const slug = (s: string | undefined) => (s ?? '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
const base = (path: string | undefined) => (path ?? '').replace(/[\\/]+$/, '').split(/[\\/]/).pop() ?? '';

/** Whether a workspace from `workspaces.list` is this root: by id, by the root id it names, or by name. */
export function workspaceIsRoot(ws: Workspace, projectId: string, root: { rootId: string; name?: string; workspaceId?: string }): boolean {
  if (ws.projectId && ws.projectId !== projectId) return false;
  if (ws.rootId) return ws.rootId === root.rootId;
  if (root.workspaceId && ws.id === root.workspaceId) return true;
  if (ws.id === root.rootId) return true;
  const names = [slug(ws.name), slug(base(ws.root))].filter(Boolean);
  return names.includes(slug(root.rootId)) || (!!root.name && names.includes(slug(root.name)));
}

/**
 * Where a root lives, in words: "Local", "WSL · Ubuntu", "Dev container", "SSH · build-box",
 * "Project node". Undefined when nothing says.
 */
export function targetWords(r: { target?: string; targetKind?: string; distro?: string }): string | undefined {
  const [kindFromTarget, ...rest] = (r.target ?? '').split(':');
  const kind = r.targetKind ?? (kindFromTarget || (r.distro ? 'wsl' : undefined));
  const detail = r.distro ?? (rest.join(':') || undefined);
  switch (kind) {
    case 'local':
      return 'Local';
    case 'wsl':
      return detail ? `WSL · ${detail}` : 'WSL';
    case 'devcontainer':
      return 'Dev container';
    case 'ssh':
      return detail ? `SSH · ${detail}` : 'SSH';
    case 'node':
      return 'Project node';
    case undefined:
      return undefined;
    default:
      return r.target;
  }
}

export type HostMark = { state: 'connected' | 'working' | 'offline' | 'error' | 'idle'; label: string };

/** The host state anything says: a live workspace without a state reads as ready (the flat `workspaces.list` of today). */
function stateOf(r: MachineRoot): string | undefined {
  return r.workspace ? (r.workspace.state ?? r.hostState ?? 'ready') : r.hostState;
}

/**
 * A root's host in words, for a `StatusMark`. The live answer wins; a platform record alone says
 * only what is down (a running state there is as old as the last registration). Undefined when
 * there is nothing worth saying.
 */
export function hostMark(r: MachineRoot): HostMark | undefined {
  const state = stateOf(r);
  switch (state) {
    case 'ready':
      return r.live ? { state: 'connected', label: 'Running' } : undefined;
    case 'starting':
    case 'provisioning':
      return { state: 'working', label: 'Starting' };
    case 'stopped':
    case 'exited':
      return { state: 'offline', label: r.distro || r.targetKind === 'wsl' || r.target?.startsWith('wsl') ? 'Distro stopped' : 'Stopped' };
    case 'error':
      return { state: 'error', label: 'Host failed' };
  }
  if (r.targetState === 'stopped') return { state: 'offline', label: 'Host stopped' };
  if (r.targetState === 'unavailable') return { state: 'offline', label: 'Unavailable' };
  // The app answered and no host was started for this root this run.
  if (r.live) return { state: 'idle', label: 'Not started' };
  return undefined;
}

/** Whether a root's host runs now, as far as anything live says. */
export function rootRunning(r: MachineRoot): boolean {
  return (r.live || !!r.workspace) && stateOf(r) === 'ready';
}

/** A remote as a person reads it: no scheme, no credentials, no `.git`. */
export function remoteWords(remote: string | undefined): string | undefined {
  if (!remote) return undefined;
  return remote
    .replace(/^[a-z+]+:\/\//i, '')
    .replace(/^[^@/]+@/, '')
    .replace(/\.git$/, '')
    .replace(/^([^/:]+):(?!\d)/, '$1/');
}

function fromPlatform(r: ProjectRoot, registered: RegisteredWorkspace | undefined): MachineRoot {
  return {
    rootId: r.rootId,
    name: registered?.name ?? r.rootId,
    remote: r.remote ?? registered?.remote,
    branch: r.branch ?? registered?.branch,
    targetKind: registered?.target,
    distro: r.distro ?? registered?.distro,
    hostState: r.hostState ?? registered?.hostState,
    targetState: r.targetState ?? registered?.targetState,
    lastSeenAt: r.lastSeenAt,
    live: false,
  };
}

function fromLive(r: LiveProjectRoot, platform: ProjectRoot | undefined, registered: RegisteredWorkspace | undefined): MachineRoot {
  return {
    rootId: r.rootId,
    name: r.name ?? registered?.name ?? r.rootId,
    remote: platform?.remote ?? registered?.remote,
    branch: platform?.branch ?? registered?.branch,
    target: r.target,
    targetKind: r.targetKind ?? registered?.target,
    distro: r.distro ?? platform?.distro ?? registered?.distro,
    hostState: r.hostState,
    targetState: r.targetState,
    lastSeenAt: platform?.lastSeenAt,
    workspaceId: r.workspaceId,
    live: true,
  };
}

export interface MergeInput {
  deviceId: string;
  /** `projects.list`; undefined when the app does not have it (or is not connected). */
  live?: LiveProject[];
  /** The platform's projects the person may see, each with all its roots; undefined while loading or on failure. */
  platform?: PlatformProject[];
  /** `workspaces.list`, flattened. */
  workspaces?: Workspace[];
  /** The registration's workspaces (`DeviceRow.workspaces`). */
  registered?: RegisteredWorkspace[];
  /** `workbench.status`'s workspace: what is open on the workbench. */
  openWorkspace?: { id: string; name: string; root?: string };
}

export interface MergeResult {
  projects: MachineProject[];
  /** Workspaces `workspaces.list` named that match no project's root: shown on their own so nothing live disappears. */
  unmatched: Workspace[];
}

export function mergeMachineProjects(input: MergeInput): MergeResult {
  const { deviceId, live, platform, workspaces = [], registered = [], openWorkspace } = input;
  const platformById = new Map((platform ?? []).map((p) => [p.id, p]));
  const regOf = (projectId: string, rootId: string) => registered.find((w) => w.id === rootId && (!w.projectId || w.projectId === projectId));
  const out: MachineProject[] = [];

  if (live) {
    for (const p of live) {
      const plat = platformById.get(p.id);
      // An organisation project the platform does not list for this person stays out: they may not see it.
      // (While the platform has not answered, nothing is held back.)
      if (p.organisation && platform && !plat) continue;
      const roots = p.roots.map((r) => fromLive(r, plat?.roots.find((x) => x.rootId === r.rootId), regOf(p.id, r.rootId)));
      // Roots the platform has from this device that the app did not name (moved, or registered by an older run).
      for (const r of plat?.roots ?? []) if (r.deviceId === deviceId && !roots.some((x) => x.rootId === r.rootId)) roots.push(fromPlatform(r, regOf(p.id, r.rootId)));
      out.push({ id: p.id, name: p.name, open: p.open, organisation: !!plat || !!p.organisation, roots });
    }
    // The platform's projects with a root from this device that the app no longer names.
    for (const plat of platform ?? []) {
      if (out.some((p) => p.id === plat.id)) continue;
      const mine = plat.roots.filter((r) => r.deviceId === deviceId);
      if (mine.length) out.push({ id: plat.id, name: plat.name, open: false, organisation: true, roots: mine.map((r) => fromPlatform(r, regOf(plat.id, r.rootId))) });
    }
  } else {
    for (const plat of platform ?? []) {
      const mine = plat.roots.filter((r) => r.deviceId === deviceId);
      if (mine.length) out.push({ id: plat.id, name: plat.name, open: false, organisation: true, roots: mine.map((r) => fromPlatform(r, regOf(plat.id, r.rootId))) });
    }
  }

  // The live overlay: each workspace `workspaces.list` names lands on its root.
  const used = new Set<string>();
  for (const p of out) {
    for (const r of p.roots) {
      const ws = workspaces.find((w) => !used.has(w.id) && workspaceIsRoot(w, p.id, r));
      if (!ws) continue;
      used.add(ws.id);
      r.workspace = ws;
      r.live = true;
      r.target ??= ws.target;
      r.targetKind ??= ws.targetKind;
      r.distro ??= ws.distro;
    }
  }

  // Which project is open: `projects.list` says; otherwise the focused workspace's (or the status's) root.
  if (!out.some((p) => p.open)) {
    const focused = workspaces.find((w) => w.focused) ?? workspaces[0];
    const open = out.find(
      (p) =>
        p.roots.some((r) => r.workspace && (r.workspace.id === focused?.id || r.workspace.id === openWorkspace?.id)) ||
        (!!focused && p.roots.some((r) => workspaceIsRoot(focused, p.id, r))) ||
        (!!openWorkspace && p.roots.some((r) => workspaceIsRoot({ id: openWorkspace.id, name: openWorkspace.name, root: openWorkspace.root }, p.id, r))) ||
        (!!openWorkspace && slug(p.name) === slug(openWorkspace.name)),
    );
    if (open) open.open = true;
  }

  out.sort((a, b) => Number(b.open) - Number(a.open) || a.name.localeCompare(b.name));
  return { projects: out, unmatched: workspaces.filter((w) => !used.has(w.id)) };
}
